import { Component, inject, OnInit, Type, VERSION } from '@angular/core';
import { CommonModule } from "@angular/common";
import { RouterModule } from '@angular/router';
import { FormsModule } from "@angular/forms";
// Material
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
// PrimeNG
import { ToggleSwitchModule } from 'primeng/toggleswitch';
// EZ
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormDialogService } from '@template-ui/services/ez-form-dialog.service';
import { EzFormDialogButtonConfig, EzFormDialogMode, IEzFormWrapper } from '@template-ui/types';
// App shell
import { AppBaseComponent } from "../app-base.component";
import { DevInfoTabsComponent } from '../dev-info-tabs/dev-info-tabs.component';
import { StatusBarService } from "../../../core/services/status-bar.service";
import { RoleService } from "../../services/roles.service";
import { SettingsService } from "../../services/settings.service";

/**
 * Footer component for the app shell.
 * Displays version/build info and opens app properties dialog.
 */
@Component({
  selector: 'app-footer',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    ToggleSwitchModule,
    UiLibraryModule
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent extends AppBaseComponent implements OnInit {
  // ───────────────────────────────────────────────────────────────
  //  Public Template‑Bound Properties
  // ───────────────────────────────────────────────────────────────
  currentYear: number = new Date().getFullYear();
  version: string | undefined = '';
  versionAndBuild: string = '';
  branch?: string = 'branch';
  commit?: string = 'commit';
  buildReason?: string = 'reason';

  // ───────────────────────────────────────────────────────────────
  //  Dependency Injection
  // ───────────────────────────────────────────────────────────────
  statusBarService = inject(StatusBarService);
  settingsService = inject(SettingsService);
  role = inject(RoleService);
  private readonly ezFormDialog = inject(EzFormDialogService);
  private angVer = { angularVersion: VERSION.full };
  private readonly appPropsHeaderButtons = [
    { id: 'close', buttonText: 'Close', closesDialog: true },
  ] satisfies EzFormDialogButtonConfig[];

  // ───────────────────────────────────────────────────────────────
  //  Lifecycle Hooks
  // ───────────────────────────────────────────────────────────────
  constructor() {
    super();
  }

  ngOnInit(): void {
    try {
      this.version = this.appConfigService.appConfig?.pipeline.BuildNumber;
      const envName = this.appConfigService.appConfig?.pipeline.EnvironmentName;
      this.versionAndBuild = `Ver: <b>${envName} ${this.version}</b>`;
    } catch (e: unknown) {
      this.version = 'Error using fake Http client';
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Public Methods
  // ───────────────────────────────────────────────────────────────
  /**
   * Opens a dialog showing application properties.
   * @param event Click event
   */
  onInfoClick(event: unknown): void {
    const props = this.filterPropsForDevInfo({
      ...this.appConfigService.appConfig?.env,
      ...this.angVer,
      ...this.appConfigService.appConfig?.pipeline,
      ...this.appConfigService.appConfig?.oidc
    });
    void this.ezFormDialog.open({
      mode: EzFormDialogMode.DISPLAY,
      header: 'Application Info',
      width: '1000px',
      height: '760px',
      disableClose: false,
      headerButtons: this.appPropsHeaderButtons,
      formComponent: DevInfoTabsComponent as unknown as Type<IEzFormWrapper<Record<string, unknown>>>,
      formComponentInputs: {
        SomeObject: props,
        useTable: true,
      },
    });
  }

  private filterPropsForDevInfo(props: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(props).filter(([key]) => !key.startsWith(' '))
    );
  }
}
